import { OUTSTANDING_INQUIRY_STATUSES } from "../../../../api/supplierInquiriesApi"
import type { SupplierDashboardStats } from "../../../../api/types"
import type {
  DashboardTranslateFn,
  DashboardWidgetDef,
} from "../../../shared/dashboard/DashboardBoardShell"
import { formatInt, type DashboardWidgetKey } from "./supplierDashboardStore"
import { CoverageWidget } from "./widgets/CoverageWidget"
import { FrameworksWidget } from "./widgets/FrameworksWidget"
import { HandedBackWidget } from "./widgets/HandedBackWidget"
import { InReviewWidget } from "./widgets/InReviewWidget"
import { OutstandingWidget } from "./widgets/OutstandingWidget"
import { ReplyQualityWidget } from "./widgets/ReplyQualityWidget"

const MC_BASE = "/material-compliance"

export type TranslateFn = DashboardTranslateFn

export interface DashboardWidgetProps {
  stats: SupplierDashboardStats
  /** The dashboard did not load: `stats` may be the zero fallback. */
  loadFailed: boolean
}

export const DASHBOARD_WIDGETS: Record<
  DashboardWidgetKey,
  DashboardWidgetDef<SupplierDashboardStats>
> = {
  coverage: {
    titleKey: "MC_SSD_W_COVERAGE",
    wide: true,
    component: CoverageWidget,
    // Master-data suppliers list, not the declarations inbox — "All
    // suppliers" means the supplier records, not their declarations.
    linkTo: `${MC_BASE}/master-data/suppliers`,
    linkLabelKey: "MC_SSD_W_COVERAGE_LINK",
  },
  frameworks: {
    titleKey: "MC_SSD_W_FRAMEWORKS",
    component: FrameworksWidget,
    countText: (_s, translate) => translate("MC_SSD_W_FRAMEWORKS_COUNT"),
    linkTo: `${MC_BASE}/supplier-studio/declarations`,
    linkLabelKey: "MC_SSD_W_FRAMEWORKS_LINK",
  },
  inReview: {
    titleKey: "MC_SSD_W_IN_REVIEW",
    component: InReviewWidget,
    // No "x / y" here: the top list counts declarations while the bucket counts
    // suppliers — mixing the two reads as a bug (e.g. "4 / 2"). Follow-up: expose
    // a declarations-in-review total in the payload if a denominator is wanted.
    countText: undefined,
    linkTo: `${MC_BASE}/supplier-studio/declarations?lifecycle=pending,processing,ready`,
    linkLabelKey: "MC_SSD_W_IN_REVIEW_LINK",
  },
  outstanding: {
    titleKey: "MC_SSD_W_OUTSTANDING",
    component: OutstandingWidget,
    // Both sides are inquiry-level counts now (outstandingTop is the top-N
    // of the same waiting+reminded+escalated+ureview set outstandingTotal
    // counts, i.e. OUTSTANDING_INQUIRY_STATUSES — matching the link below),
    // so "N / total" is an apples-to-apples top-N-of-total, not a
    // supplier-bucket vs. inquiry-count mismatch.
    countText: (s, _translate, locale) =>
      `${formatInt(s.outstandingTop.length, locale)} / ${formatInt(s.outstandingTotal, locale)}`,
    linkTo: `${MC_BASE}/supplier-studio/inquiry?status=${OUTSTANDING_INQUIRY_STATUSES.join(",")}`,
    linkLabelKey: "MC_SSD_W_OUTSTANDING_LINK",
  },
  replyQuality: {
    titleKey: "MC_SSD_W_REPLY_QUALITY",
    component: ReplyQualityWidget,
    // Card-level link stays unfiltered — the per-category filter now lives
    // on each row link (see F4-FE).
    linkTo: `${MC_BASE}/supplier-studio/declarations`,
    linkLabelKey: "MC_SSD_W_IN_REVIEW_LINK",
  },
  handedBack: {
    titleKey: "MC_SSD_W_HANDED_BACK",
    component: HandedBackWidget,
    countText: (s, _translate, locale) =>
      formatInt(
        s.handedBack.escalated +
          s.handedBack.undeliverable +
          s.handedBack.complained,
        locale,
      ),
    linkTo: `${MC_BASE}/supplier-studio/inquiry?status=escalated,undeliverable,complained`,
    linkLabelKey: "MC_SSD_W_OUTSTANDING_LINK",
  },
}
